export const BUILDER_TYPE_LABELS: Record<string, string> = {
  rich_text: 'Texto enriquecido',
  video: 'Video',
  pdf: 'Documento PDF',
  pdf_folder: 'Carpeta de PDF',
  image: 'Imagen',
  audio: 'Audio',
  download: 'Archivo descargable',
  html: 'HTML',
  external_link: 'Enlace externo',
  h5p: 'H5P',
  assessment: 'Evaluación',
  scorm: 'SCORM',
  xapi: 'xAPI',
  cmi5: 'cmi5',
  label: 'Etiqueta',
};

export const BUILDER_MODULE_ICONS: Record<string, string> = {
  h5p: 'extension',
  assessment: 'quiz',
  video: 'play_circle',
  pdf: 'picture_as_pdf',
  pdf_folder: 'folder',
  rich_text: 'description',
  html: 'code',
  external_link: 'link',
  download: 'download',
  image: 'image',
  audio: 'headphones',
  scorm: 'package_2',
  xapi: 'package_2',
  cmi5: 'package_2',
  label: 'label',
};

export interface BuilderH5pInstance {
  id: string;
  isVisible?: boolean;
  isRequired?: boolean;
  allowPreview?: boolean;
  unlimitedAttempts?: boolean;
  maxAttempts?: number | null;
  showSolutions?: boolean;
  allowRetry?: boolean;
  gradeWeight?: number | null;
}

export interface BuilderAssessmentInstance {
  id: string;
  isVisible?: boolean;
  isRequired?: boolean;
  unlimitedAttempts?: boolean;
  maxAttempts?: number | null;
  timeLimitSeconds?: number | null;
  timeLimitMinutes?: number | null;
  gradeWeight?: number | null;
  opensAt?: string | null;
  closesAt?: string | null;
}

export interface BuilderItem {
  id: string;
  sectionId?: string;
  courseSectionId?: string;
  itemType?: string;
  isLabel?: boolean;
  learningObjectId?: string | null;
  libraryUrl?: string | null;
  title?: string;
  titleOverride?: string | null;
  sortOrder?: number;
  isVisible?: boolean;
  displayTitle: string;
  type?: string;
  typeValue?: string | null;
  version?: number | null;
  settings?: Record<string, unknown>;
  availableFrom?: string | null;
  availableUntil?: string | null;
  requiresItemId?: string | number | null;
  h5pInstance?: BuilderH5pInstance | null;
  assessmentInstance?: BuilderAssessmentInstance | null;
}

export interface BuilderSection {
  id: string;
  title: string;
  sortOrder?: number;
  isVisible?: boolean;
  items: BuilderItem[];
}

export interface BuilderResponse {
  course?: { id: string; name?: string; title?: string };
  sections?: BuilderSection[];
}

export interface LibraryPickerItem {
  id: string;
  name: string;
  type: string;
  typeValue: string;
  version?: number;
  usageCount?: number;
}

export interface LibraryPickerMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export function moduleNumber(index: number) {
  return String(index + 1).padStart(2, '0');
}

export function moduleIcon(typeValue?: string | null) {
  if (!typeValue) return 'article';
  return BUILDER_MODULE_ICONS[typeValue] ?? 'article';
}

export function sectionsFromResponse(data: BuilderSection[] | BuilderResponse | undefined): BuilderSection[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return (data.sections ?? []).map((section) => ({
    ...section,
    items: section.items ?? [],
  }));
}

export function cloneSections(sections: BuilderSection[]): BuilderSection[] {
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) => ({
      ...item,
      h5pInstance: item.h5pInstance ? { ...item.h5pInstance } : item.h5pInstance,
      assessmentInstance: item.assessmentInstance ? { ...item.assessmentInstance } : item.assessmentInstance,
    })),
  }));
}

export interface PrerequisiteOption {
  id: string;
  label: string;
}

export function buildPrerequisiteOptions(sections: BuilderSection[], currentItem: BuilderItem): PrerequisiteOption[] {
  const options: PrerequisiteOption[] = [];
  for (const section of sections) {
    for (const item of section.items) {
      if (item.isLabel || item.id === currentItem.id) continue;
      options.push({
        id: item.id,
        label: `${section.title}: ${item.displayTitle}`,
      });
    }
  }
  return options;
}

export function formatDateTimeLocal(value?: string | null) {
  if (!value) return '';
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  return normalized.slice(0, 16);
}

/** Hora y fecha corta para banners del constructor: `22:57 01-09-26`. */
export function formatBuilderAvailabilityDateTime(value?: string | null): string {
  if (!value) return '';
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return value.replace('T', ' ').slice(0, 16);
  }
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${hours}:${minutes} ${day}-${month}-${year}`;
}

export function resolvePrerequisiteLabel(
  sections: BuilderSection[],
  requiresItemId?: string | number | null,
): string | null {
  if (!requiresItemId) return null;
  const id = String(requiresItemId);
  for (const section of sections) {
    const match = section.items.find((row) => row.id === id);
    if (match) return match.displayTitle;
  }
  return null;
}

export function itemRestrictionBanners(item: BuilderItem, sections: BuilderSection[] = []) {
  const banners: Array<{ prefix: string; highlight?: string; highlightEnd?: string }> = [];
  if (item.availableFrom) {
    banners.push({ prefix: 'Disponible desde', highlight: formatBuilderAvailabilityDateTime(item.availableFrom) });
  }
  if (item.availableUntil) {
    banners.push({ prefix: 'Disponible hasta', highlight: formatBuilderAvailabilityDateTime(item.availableUntil) });
  }
  if (item.requiresItemId) {
    const requiredTitle = resolvePrerequisiteLabel(sections, item.requiresItemId);
    if (requiredTitle) {
      banners.push({ prefix: 'Requiere completar', highlight: requiredTitle });
    } else {
      banners.push({ prefix: 'Requiere completar actividad previa' });
    }
  }
  return banners;
}
